import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import SelectBox from "components/atoms/SelectBox";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Button from "components/atoms/button/Button";
import useLoading from "components/atoms/loading/useLoading";
import GeneralText from "components/atoms/text/GeneralText";
import useToast from "components/atoms/toast/useToast";

import { useSelectBoxOptions } from "hooks/queries/useOptions";
import { useResearchAnalysis, useResearchAnalysisUpdate } from "hooks/queries/useResearch";
import { useUser } from "hooks/queries/useUser";

import { getDateFromString, getTimeFromString } from "utils/dateUtil";
import { apiUrl, wsUrl } from "utils/urls";

import axios from "axios";
import saveAs from "file-saver";

const Container = styled.div`
    width: 100%;
    max-height: calc(100% - 24px);
`;

const Row = styled.div`
    display: flex;
    justify-content: ${(props) => props.justiContent || "flex-start"};
    align-items: ${(props) => props.alignItems || "flex-start"};
`;

const ContentsWrap = styled.div`
    display: flex;
    margin: 24px 0 0 0;
    min-height: calc(100vh - 500px);
    max-height: calc(100vh - 500px);
`;

const ListSection = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 16px 0 0;
    width: 200px;
`;

const ListWrap = styled.div`
    display: flex;
    flex-direction: column;
    margin: 8px 0 0 0;
    padding: 8px;
    width: calc(100% - 8px);
    height: 100%;
    max-height: calc(100% - 48px);
    background-color: var(--color-Base1);
    border-radius: 4px;
    box-sizing: border-box;
    overflow: auto;
`;

const ListItem = styled.button`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    border-radius: 4px;
    margin: 0 0 2px 0;
    padding: 0 16px;
    width: 100%;
    min-height: 48px;
    background-color: ${(props) => (props.active ? "var(--color-Key)" : "transparent")};
    border: transparent;
    transition: all 120ms ease-in;

    &:hover {
        background-color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-ButtonHover4)")};
    }

    p {
        color: ${(props) => (props.active ? "var(--color-White)" : "var(--color-Black")};
    }

    svg {
        fill: ${(props) => (props.active ? "var(--color-White)" : "var(--color-Black")};
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    margin: 0 0 0 16px;
`;

const ContentsSection = styled.div`
    flex: 1;
`;

const ItemView = styled.div`
    width: 100%;
    height: 100%;
`;

const TabHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const TabSection = styled.div`
    display: flex;
    align-items: center;
`;

const TabButton = styled.button`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 40px;
    background-color: var(--color-White);
    border: Transparent;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-DisabledInput)")};
    }
`;

const TabViewSection = styled.div`
    margin: 16px 0 0 0;
    width: 100%;
    height: 100%;
    max-height: calc(100% - 56px);
    overflow: auto;
`;

const RelResearchView = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const RelParagraphWrap = styled.div`
    width: 100%;
    height: 100%;
`;

const RelParagraph = styled.div`
    margin: 0;
    padding: 4px;
    width: 100%;
    height: calc(100% - 8px);
    border: transparent;
    border-radius: 4px;
    background-color: var(--color-White);
    text-align: justify;
    line-height: 1.4;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-SubBlack);
    resize: none;
    white-space: pre-wrap;

    &:focus {
        outline: none;
        background-color: var(--color-Base1);
    }

    &:disabled {
        cursor: text;
    }
`;

const ReferenceView = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const ButtonSection = styled.div`
    display: flex;
    align-items: center;
`;

const ReferenceListWrap = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: auto;
`;

const RefListItemWrap = styled.div`
    padding: 8px 0;
    width: 100%;
    border-bottom: dashed 1px var(--color-Line);
`;

const AnalysisView = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const StopButtonWrap = styled.div`
    position: absolute;
    bottom: 0px;
    left: 50%;
    transform: translate(-50%, -50%);
`;

function ResearchAnalysisModal({ paperLength, research, user }) {
    const [activeView, setActiveView] = useState("analysis");
    const [activeTab, setActiveTab] = useState("related");
    const [analysisList, setAnalysisList] = useState([]);
    const [analysis, setAnalysis] = useState("");
    const [analyzing, setAnalyzing] = useState(false);
    const [language, setLanguage] = useState("");
    const [languageOptions, setLanguageOptions] = useState([]);

    const [style, setStyle] = useState("");
    const [styleList, setStyleList] = useState([]);
    const [styledTexts, setStyledTexts] = useState([]);

    const userQuery = useUser();
    const optionsQuery = useSelectBoxOptions();
    const analysisQuery = useResearchAnalysis({ research: research.id });
    const analysisMutation = useResearchAnalysisUpdate();

    const { setToast } = useToast();
    const { setLoading } = useLoading();

    const wsRef = useRef(null);

    const activeItem = analysisList.find((item) => item.id === activeView);

    useEffect(() => {
        analysisQuery.remove();
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, []);

    useEffect(() => {
        if (userQuery.data) {
            setLanguage(userQuery.data.env_ai_language);
            setStyle(userQuery.data.env_citation_style);
        }
        if (optionsQuery.data) {
            setLanguageOptions(optionsQuery.data.env_ai_language);
            setStyleList(optionsQuery.data.env_citation_style);
        }
        if (analysisQuery.data) {
            setAnalysis("");
            setAnalysisList([...analysisQuery.data]);
        }
    }, [userQuery.data, optionsQuery.data, analysisQuery.data]);

    useEffect(() => {
        if (activeItem) {
            if (activeItem.bibliography_bibtex.length > 0) {
                handleChangeStyle(style);
            }
        }
    }, [activeItem]);

    const createAnalysis = () => {
        setLoading(true);
        wsRef.current = new WebSocket(wsUrl("researchAnalysisConsumer"));

        wsRef.current.onerror = (error) => {
            console.log(error);
        };

        wsRef.current.onopen = () => {
            setAnalyzing(true);
            wsRef.current.send(
                JSON.stringify({
                    action: "analyze",
                    language: "ko",
                    research_id: research.id,
                }),
            );
        };

        wsRef.current.onmessage = (evt) => {
            const data = JSON.parse(evt.data);

            // 리서치 설명에 데이터를 더한다.
            if (data.content === "<LIST>") {
                setAnalysis("");
                setAnalysisList(data.analysis_list);
            } else if (data.content === "<START>") {
                setLoading(false);
                setActiveTab("related");
                setActiveView(data.analysis_id);
            } else if (data.content === "<END>") {
                wsRef.current.close();
            } else {
                // research state의 desc 필드에 data.content(String) 를 더한다.
                setAnalysis((prev) => prev + data.content);
            }
        };

        wsRef.current.onclose = (event) => {
            setLoading(false);
            wsRef.current = null;
            setAnalyzing(false);
            if (event.code !== 1000) {
                // 정상 종료가 아닌 경우
                console.log(event);
                Alert("warn", "에러", "연결이 끊겼습니다.\n잠시 후 새로고침 해주세요.");
            }
        };
    };

    let analysisText = (related_research) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            return analysis;
        } else if (!related_research) {
            return analysis;
        } else {
            return related_research;
        }
    };

    const handleCopyButtonClick = (text) => {
        navigator.clipboard
            .writeText(styledTexts.join("\n"))
            .then(() => {
                setToast("복사되었습니다.", "info");
            })
            .catch((error) => {
                console.error("복사 실패:", error);
            });
    };

    const handleDownloadBib = (bibtex) => {
        if (bibtex.length > 0) {
            saveAs(new Blob([bibtex.join("\n")]), `paper-${new Date().toISOString()}.bib`);
        } else {
            Alert("warn", "에러", "다운로드할 참고문헌이 없습니다");
        }
    };

    const handleChangeStyle = (style) => {
        setLoading(true);
        axios
            .post(apiUrl("paperStyle"), {
                style_type: style,
                bib_list: activeItem.bibliography_bibtex,
            })
            .then((res) => {
                setLoading(false);
                setStyle(style);
                setStyledTexts(res.data);
            })
            .catch((err) => {
                setLoading(false);
                Alert("error", "에러", "스타일 변경에 실패했습니다.");
            });
        setLoading(false);
    };

    return (
        <Container>
            <GeneralText size={"regular"}>
                리서치에 연결된 참고문헌을 자동 분석하여 관련연구와 참고문헌을 작성합니다.
            </GeneralText>
            <ContentsWrap>
                <ListSection>
                    <Button
                        onClick={() => setActiveView("analysis")}
                        active={activeView === "analysis"}
                        margin={"0 8px 0 0"}
                        width={"calc(100% - 8px)"}
                        height={"32px"}
                        bgColor={"var(--color-Button1)"}
                        hoverBgColor={"var(--color-ButtonHover1)"}
                        fontColor={"var(--color-White)"}
                        buttonText={"새 참고문헌 자동분석"}
                    >
                        <Icon name={"plus"} color={"var(--color-White)"} />
                    </Button>
                    <ListWrap>
                        {analysisList.map((item) => (
                            <ListItem
                                key={item.id}
                                onClick={() => setActiveView(item.id)}
                                active={activeView === item.id}
                            >
                                <Icon
                                    name={"ai"}
                                    size={"12"}
                                    color={activeView === item.id ? "var(--color-White)" : "var(--color-SubBlack)"}
                                />
                                <Column>
                                    <GeneralText size={"regular"} color={"var(--color-Black)"}>
                                        참고문헌 {item.bibliography_bibtex?.length}건
                                    </GeneralText>
                                    <Row style={{ margin: "2px 0 0 0" }}>
                                        <GeneralText size={"small"} margin={"0 8px 0 0"} color={"var(--color-Grey1)"}>
                                            {getDateFromString(item.created_at)}
                                        </GeneralText>
                                        <GeneralText size={"small"} margin={"0"} color={"var(--color-Grey1)"}>
                                            {getTimeFromString(item.created_at)}
                                        </GeneralText>
                                    </Row>
                                </Column>
                            </ListItem>
                        ))}
                    </ListWrap>
                </ListSection>
                <ContentsSection>
                    {activeView === "analysis" ? (
                        <AnalysisView>
                            <GeneralText size={"regular"}>리서치에 연결된 참고문헌들을 분석하여</GeneralText>
                            <GeneralText size={"regular"}>관련연구와 참고문헌을 작성해드립니다.</GeneralText>
                            <GeneralText size={"regular"} margin={"24px 0 40px; 0"}>
                                관련연구를 작성할 언어를 선택해주세요.
                            </GeneralText>
                            <ButtonSection>
                                <SelectBox
                                    width="100px"
                                    height="32px"
                                    placeholder="언어 선택"
                                    items={languageOptions}
                                    value={language}
                                    onChange={(e) => setLanguage(e)}
                                />
                                <Button
                                    margin={"0 0 0 8px"}
                                    height={"32px"}
                                    bgColor={"var(--color-Button2)"}
                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                    fontColor={"var(--color-White)"}
                                    buttonText={"자동 분석 시작"}
                                    onClick={createAnalysis}
                                    disabled={analyzing}
                                >
                                    <Icon name={"ai"} size={"12"} color="var(--color-White)" />
                                </Button>
                            </ButtonSection>
                        </AnalysisView>
                    ) : (
                        activeItem && (
                            <ItemView key={activeItem.id} active={activeView === activeItem.id}>
                                <TabHeader>
                                    <TabSection>
                                        <TabButton
                                            onClick={() => setActiveTab("related")}
                                            active={activeTab === "related"}
                                        >
                                            <GeneralText
                                                size={"regular"}
                                                color={
                                                    activeTab === "related"
                                                        ? "var(--color-Key)"
                                                        : "var(--color-DisabledText)"
                                                }
                                            >
                                                관련연구
                                            </GeneralText>
                                        </TabButton>
                                        <TabButton
                                            onClick={() => setActiveTab("reference")}
                                            active={activeTab === "reference"}
                                        >
                                            <GeneralText
                                                size={"regular"}
                                                color={
                                                    activeTab === "reference"
                                                        ? "var(--color-Key)"
                                                        : "var(--color-DisabledText)"
                                                }
                                            >
                                                참고문헌
                                            </GeneralText>
                                        </TabButton>
                                    </TabSection>
                                    {activeTab === "related" ? (
                                        <ButtonSection>
                                            <Tooltip message={"복사"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"var(--color-Button2)"}
                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                    onClick={(e) =>
                                                        handleCopyButtonClick(analysisText(activeItem.related_research))
                                                    }
                                                >
                                                    <Icon name={"copy"} size={"12"} color={"var(--color-White)"} />
                                                </Button>
                                            </Tooltip>
                                        </ButtonSection>
                                    ) : (
                                        <ButtonSection>
                                            <GeneralText
                                                size={"regular"}
                                                margin={"0 8px 0 0"}
                                                color={"var(--color-Grey1)"}
                                            >
                                                스타일
                                            </GeneralText>
                                            <SelectBox
                                                width={"140px"}
                                                height={"32px"}
                                                placeholder="스타일"
                                                items={styleList}
                                                value={style}
                                                onChange={(e) => handleChangeStyle(e)}
                                            />
                                            <Tooltip message={"복사"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"var(--color-Button2)"}
                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                    margin={"0 8px"}
                                                    onClick={(e) =>
                                                        handleCopyButtonClick(activeItem.bibliography_bibtex)
                                                    }
                                                >
                                                    <Icon name={"copy"} size={"12"} color={"var(--color-White)"} />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip message={"다운로드"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"var(--color-Button2)"}
                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                    onClick={(e) => handleDownloadBib(activeItem.bibliography_bibtex)}
                                                >
                                                    <Icon name={"download"} size={"12"} color={"var(--color-White)"} />
                                                </Button>
                                            </Tooltip>
                                        </ButtonSection>
                                    )}
                                </TabHeader>
                                <TabViewSection>
                                    {activeTab === "related" ? (
                                        <RelResearchView>
                                            <RelParagraphWrap>
                                                <RelParagraph>
                                                    {analysisText(activeItem.related_research)}
                                                    {analyzing && <AiCursor />}
                                                </RelParagraph>
                                            </RelParagraphWrap>
                                            {analyzing && (
                                                <StopButtonWrap>
                                                    <Button
                                                        width={"120px"}
                                                        height={"32px"}
                                                        bgColor={"var(--color-Button2)"}
                                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                                        fontColor={"var(--color-White)"}
                                                        buttonText={"작성 중지"}
                                                        onClick={(e) => {
                                                            analysisMutation.mutate({
                                                                id: activeItem.id,
                                                                related_research: analysis,
                                                            });
                                                            wsRef.current.send(JSON.stringify({ action: "stop" }));
                                                        }}
                                                    >
                                                        <Icon name={"stop"} color={"var(--color-White)"} />
                                                    </Button>
                                                </StopButtonWrap>
                                            )}
                                        </RelResearchView>
                                    ) : (
                                        <ReferenceView>
                                            <ReferenceListWrap>
                                                {styledTexts.map((styledText, index) => (
                                                    <RefListItemWrap key={index}>
                                                        <GeneralText size={"regular"} color={"var(--color-Grey1)"}>
                                                            {styledText}
                                                        </GeneralText>
                                                    </RefListItemWrap>
                                                ))}
                                            </ReferenceListWrap>
                                        </ReferenceView>
                                    )}
                                </TabViewSection>
                            </ItemView>
                        )
                    )}
                </ContentsSection>
            </ContentsWrap>
        </Container>
    );
}

export default ResearchAnalysisModal;
